import { Shield, AlertTriangle, AlertCircle, Info } from 'lucide-react';

interface SgisBadgeProps {
  score: number;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
}

/**
 * SGIS (ShadowGuard Intelligence Score) Badge
 * 
 * Displays 0-100 threat score with color coding:
 * - 0-29: Low (gray)
 * - 30-59: Medium (yellow)
 * - 60-79: High (orange)
 * - 80-100: Critical (red)
 */
export function SgisBadge({ score, size = 'md', showLabel = true }: SgisBadgeProps) {
  const getRiskLevel = (score: number): { level: string; color: string; icon: typeof Shield } => {
    if (score >= 80) return { level: 'Critical', color: 'red', icon: AlertCircle };
    if (score >= 60) return { level: 'High', color: 'orange', icon: AlertTriangle };
    if (score >= 30) return { level: 'Medium', color: 'yellow', icon: Info };
    return { level: 'Low', color: 'gray', icon: Shield };
  };

  const { level, color, icon: Icon } = getRiskLevel(score);

  const sizeClasses = {
    sm: 'text-xs px-2 py-1',
    md: 'text-sm px-3 py-1.5',
    lg: 'text-base px-4 py-2',
  };

  const iconSizes = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5',
  };

  const colorClasses = {
    red: 'bg-red-500/20 text-red-400 border-red-500/30',
    orange: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    yellow: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    gray: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
  };

  return (
    <div 
      className={`inline-flex items-center gap-2 rounded-lg border font-semibold ${sizeClasses[size]} ${colorClasses[color]}`}
      title={`SGIS Score: ${score}/100 (${level} Risk)`}
    >
      <Icon className={iconSizes[size]} />
      <span>{score}</span>
      {showLabel && <span className="font-normal opacity-75">SGIS</span>}
    </div>
  );
}
